import React, { useMemo } from "react";
import { useI18n } from "bananas-commerce-admin";

import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

import { PublicationPricesPerDaySchema, PublicationSchema } from "../../types";

import { PublicationDaysManager } from "./PublicationDaysManager";
import {
  PublicationPricesPerDayEditable,
  toPublicationPricesPerDayForTracking,
} from "./types";

export interface AddPublicationDialogProps {
  open: boolean;
  publications: PublicationSchema[];
  isLoadingPublications: boolean;
  publicationsPrices: PublicationPricesPerDayEditable[];
  onClose: () => void;
  onAdd: (abbreviation: PublicationPricesPerDaySchema["publication"]) => void;
  onAddAll?: () => void;
}

const AddPublicationDialog: React.FC<AddPublicationDialogProps> = ({
  open,
  publications,
  isLoadingPublications,
  publicationsPrices,
  onClose,
  onAdd,
  onAddAll,
}) => {
  const { t } = useI18n();

  const availablePublications = useMemo(() => {
    const publicationPricesNonEditable = publicationsPrices.flatMap((item) => {
      const prices = toPublicationPricesPerDayForTracking(item);
      return prices ? [prices] : [];
    });
    const daysManager = new PublicationDaysManager(
      publicationPricesNonEditable,
    );
    return publications.filter((pub) =>
      daysManager.hasAvailableDays(pub.abbreviation),
    );
  }, [publications, publicationsPrices]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("Lägg till titel")}</DialogTitle>
      <DialogContent sx={{ minWidth: 400 }}>
        <Autocomplete
          clearOnBlur
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option.abbreviation === value.abbreviation
          }
          loading={isLoadingPublications}
          options={availablePublications}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Välj titel")}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingPublications && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option) => <li {...props}>{option.name}</li>}
          size="small"
          sx={{ mt: 2 }}
          onChange={(_, newValue) => {
            if (newValue) {
              onAdd(newValue.abbreviation);
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        {onAddAll && availablePublications.length > 0 && (
          <Button color="secondary" variant="text" onClick={onAddAll}>
            {t("Lägg till alla resterande")}
          </Button>
        )}
        <Button sx={{ ml: "auto" }} onClick={onClose}>
          {t("Avbryt")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPublicationDialog;
